import { useMemo } from 'react';
import { type AxiosResponse } from 'axios';

import { type Country } from '@cx-modules/constants';
import { type StateCode } from '@cx-modules/types';

import { useRepubContext } from '~/providers/repub-context';
import { CookieService } from '~/services/actions/cookie-actions';
import LocalStorageService from '~/services/actions/localstorage-actions';
import TrackingActions, {
	TRACKING_ACTIONS,
	TRACKING_CATEGORIES,
	TRACKING_EVENTS,
} from '~/services/actions/tracking-actions';
import { $http } from '~/services/util/$http';
import {
	getNameFromPointSpreadValue,
	getPointSpreadValueFromName,
	isStrictlyUSHandicap,
} from '~/utils/us-utils';

import {
	type Bet,
	type BetslipType,
	betslipType,
	type BetslipUtils,
	type BookieOdds,
	type Odds,
	type OddsFormatIs,
} from '../types';

import { useOddsUtils } from './use-odds-utils';

export const useBetslipUtils = ({
	oddsFormatIs,
}: {
	oddsFormatIs: OddsFormatIs;
}): BetslipUtils => {
	const { repub } = useRepubContext();

	const { convertDecimalOddsToFractional, convertDecimalToAmerican } =
		useOddsUtils();

	const cookieService = useMemo(() => new CookieService(), []);
	const localStorageService = useMemo(() => new LocalStorageService(), []);
	const trackingActions = useMemo(() => new TrackingActions(), []);

	const getBetMeta = (
		bet: Bet,
	): {
		betId: number;
		betName: string;
		marketName: string;
		spread: string | number;
		subeventName: string;
	} => {
		const strictlyUS = isStrictlyUSHandicap(bet);

		const { betId, marketName, name, subeventName } = bet;
		const betName = (strictlyUS && getNameFromPointSpreadValue(name)) || name;
		const spread = (strictlyUS && getPointSpreadValueFromName(name)) || '';

		return {
			betId,
			betName,
			subeventName,
			marketName,
			spread,
		};
	};

	const getBetslipAPI = (
		betIds: number[],
		countryCode: Country,
		state: StateCode | '',
		completion: (success: any, res: any) => void,
	): void => {
		const originUrl = window.location.origin;

		$http({
			method: 'GET',
			url: `${originUrl}/api/cx/v1/betslip?betIds=${betIds}`,
			headers: {
				'subdivision-code': state,
				'country-code': countryCode,
				repub,
			},
		})
			.then((res: AxiosResponse) => {
				completion({ success: true }, res.data);
			})
			.catch((e: any) => {
				console.log(e);
				completion({ success: false }, e);
			});
	};

	const getBetslipCookieArray = (): [string, string][] => {
		const betslipCookieArray = cookieService
			.select('intBetslip')
			.getArray(',', (item: string) => {
				return item.split('~');
			});

		// Ensure we filter out non-tuple data
		return betslipCookieArray.filter(c => !!c?.[1]);
	};

	// NOTE: this is very similar to getbetsOdds
	const getOdds = (odds: any, opening = false): string => {
		let betOdds;

		switch (true) {
			case oddsFormatIs.American:
				betOdds = opening ? odds.openingOddsUS : odds.oddsUs;
				if (betOdds > 0 && !betOdds.includes('+')) {
					betOdds = `+${betOdds}`;
				}
				break;
			case oddsFormatIs.Decimal:
				betOdds = opening ? odds.openingOddsDecimal : odds.oddsDecimal;
				betOdds = betOdds ? betOdds.toFixed(2) : '--';
				break;
			case oddsFormatIs.Fractional:
				betOdds = opening ? odds.openingOddsFractional : odds.oddsFractional;
				break;
		}
		return betOdds || '--';
	};

	// NOTE: this seems to be duplicating getOdds
	const _UNUSED_getTotalOdds = (odds: any, opening = false): string => {
		let betOdds;
		switch (true) {
			case oddsFormatIs.American:
				betOdds = opening ? odds.openingOddsUS : odds.oddsUs;
				break;
			case oddsFormatIs.Decimal:
				betOdds = opening ? odds.openingOddsDecimal : odds.oddsDecimal;
				betOdds = betOdds ? betOdds.toFixed(2) : '--';
				break;
			case oddsFormatIs.Fractional:
				betOdds = opening ? odds.openingOddsFractional : odds.oddsFractional;
				break;
		}
		return betOdds || '--';
	};

	const getBestOdds = (bet: any): string => {
		let betOdds;

		switch (true) {
			case oddsFormatIs.American:
				betOdds = bet.bestOddsUs || bet.oddsUs;
				if (betOdds > 0) {
					betOdds = `+${betOdds}`;
				}
				break;
			case oddsFormatIs.Decimal:
				betOdds = bet.bestOddsDecimal || bet.bestOpeningOddsDecimal;
				break;
			case oddsFormatIs.Fractional:
				betOdds = bet.bestOddsFractional || bet.oddsFractional;
				break;
		}

		return betOdds || '--';
	};

	const getBestOddsFromBet = (odds: Odds[]) => {
		if (!odds?.length) {
			return {};
		}

		const bestOdds = odds.reduce((prev, current) =>
			prev.oddsDecimal > current.oddsDecimal ? prev : current,
		);

		return {
			bestOddsDecimal: bestOdds.oddsDecimal,
			bestOddsUs: Number(bestOdds.oddsUs),
			bestOddsFractional: bestOdds.oddsFractional,
			bestBookie: bestOdds.bookmakerCode,
		};
	};

	const getLocalStorageBetslip = () =>
		localStorageService.select('intBetslip').getJSON() || {};

	const getBetSelectedBookieFromLocalStore = (betId: string): string | null => {
		const localStorageBetslip = getLocalStorageBetslip();

		if (typeof localStorageBetslip[betId] !== 'undefined') {
			return localStorageBetslip[betId].selectedBookie;
		}

		return null;
	};

	const trackBetSlip = (override = false, betSlip: any[]): void => {
		// Redeye
		let bookie = '';
		let odds = '';
		let name = '';
		let betCount = 0;

		const localStorageBetslip = getLocalStorageBetslip();
		const betslipCount = Object.keys(localStorageBetslip).length;

		if (betslipCount === 0 || override) {
			const isAcca = betSlip.length > 1;
			for (let i = 0; i < betSlip.length; i++) {
				const bet = betSlip[i];
				betCount += 1;
				const join = betCount !== 1 ? '&' : '';
				bookie += `${join}bookie${betCount}=${bet.bestOddsBookmakers}`;
				odds += `${join}bet_odds${betCount}=${bet.bestOddsDecimal}`;
				name += `${join}bet_name${betCount}=${bet.name}`;
			}

			trackingActions.eventTracking({
				event: TRACKING_EVENTS.BET_SLIP_VIEW,
				eventCategory: TRACKING_CATEGORIES.BETTING,
				eventAction: TRACKING_ACTIONS[TRACKING_EVENTS.BET_SLIP_VIEW],
				eventLabel: isAcca ? 'multi_bets' : 'single_bet',
				bet: {
					bookmaker: bookie,
					odds,
					name,
				},
				accumulator: isAcca ? 'yes' : 'no',
			});
		}
	};

	const _UNUSED_syncBetslips = (): void => {
		const cookieBetslip = cookieService.select('intBetslip').getString();
		const localStorageBetslip = getLocalStorageBetslip();
		const betsToDelete: Array<string> = [];

		for (const key in localStorageBetslip) {
			if (cookieBetslip.indexOf(key.toString()) === -1) {
				betsToDelete.push(key);
			}
		}

		for (let i = 0; i < betsToDelete.length; i++) {
			delete localStorageBetslip[betsToDelete[i]];
		}

		localStorageService.select('intBetslip').set(localStorageBetslip);
	};

	const _UNUSED_getBetslipBetIdsFromCookieArray = (): string[] => {
		const betslipBetIdsFromCookieArray = getBetslipCookieArray().map(c => c[0]);

		return betslipBetIdsFromCookieArray;
	};

	const _UNUSED_getBetCountFromBetslipCookies = (
		betslipCookieArray: Array<any>,
	): number => {
		return betslipCookieArray.length;
	};

	const calculateOddsAndBestBookie = (
		data: Bet[],
		type: BetslipType,
	): BookieOdds[] => {
		const accumulatedOdds: BookieOdds[] = [];

		let betId: number;
		let bookie: string;
		let bookmakerType: 'traditional' | 'exchange';
		let oddsDec: number;
		let oddsFrac: string;
		let oddsUs: string;
		let bookieIndex: number;

		let i: number;
		let j: number;
		let isSelected: boolean;

		const bets = data.map(bet => {
			return {
				...bet,
				...getBestOddsFromBet(bet.odds),
			};
		});

		// Loop through each bet and extract betId.
		for (i = 0; i < bets.length; i++) {
			if (
				type === betslipType.SINGLE ||
				(bets[i].uiOptions && type === betslipType.MULTIPLE)
			) {
				betId = bets[i].betId;

				// Loop through odds for each bet and extract bookie and bookie odds.
				for (j = 0; j < bets[i].odds.length; j++) {
					bookie = bets[i].odds[j].bookmakerCode;
					bookmakerType = bets[i].odds[j].bookmakerType;

					isSelected = bets[i].uiOptions.selected;
					// Dont include the bet in calculations if not selected.
					if (isSelected) {
						oddsDec = bets[i].odds[j].oddsDecimal;
						oddsFrac = bets[i].odds[j].oddsFractional;
						oddsUs = bets[i].odds[j].oddsUs;
					} else {
						oddsDec = 1;
						oddsFrac = '';
						oddsUs = '';
					}

					// Loop through current array of accumulated odds and see
					// if bookiemaker is already in the array with some odds.
					bookieIndex = accumulatedOdds
						.map(item =>
							typeof item.bookmakerCode !== 'undefined'
								? item.bookmakerCode
								: '',
						)
						.indexOf(bookie);

					// If the bookie was in the array increment their odds.
					if (bookieIndex > -1) {
						accumulatedOdds[bookieIndex].oddsDecimal *= oddsDec;
						accumulatedOdds[bookieIndex].oddsUs = convertDecimalToAmerican(
							accumulatedOdds[bookieIndex].oddsDecimal,
						);
						accumulatedOdds[bookieIndex].oddsFractional =
							convertDecimalOddsToFractional(
								accumulatedOdds[bookieIndex].oddsDecimal,
							);
						if (isSelected) {
							accumulatedOdds[bookieIndex].betIds?.push(betId);
						}

						// If the bookie wasn't found in the array then add an entry for them.
					} else {
						const tempAccaOdds: BookieOdds = {
							bookmakerCode: bookie,
							oddsDecimal: oddsDec,
							oddsFractional: oddsFrac,
							oddsUs,
							betIds: [],
							bookmakerType,
						};
						if (isSelected) {
							tempAccaOdds.betIds?.push(betId);
						}
						accumulatedOdds.push(tempAccaOdds);
					}
				}
			}
		}

		// Sort the acca entries based on odds in descending order.
		return accumulatedOdds.sort((a, b) => b.oddsDecimal - a.oddsDecimal);
	};

	return {
		getBetMeta,
		getBetslipAPI,
		getBetslipCookieArray,
		getOdds,
		getBestOdds,
		getBestOddsFromBet,
		getLocalStorageBetslip,
		getBetSelectedBookieFromLocalStore,
		trackBetSlip,
		calculateOddsAndBestBookie,
	};
};
